/**
 * usePlanEntitlements — Client-side plan entitlement flags for feature gating.
 *
 * Free: desk views + 1 paper agent + research chat + 200 paper-only Desk Credits (Phase C).
 * Free never gets live agents or live order execution.
 * Operator (starter)+: full agent chat + manual live orders.
 * Desk (advanced)+: grid/DCA + 1 live autonomous agent.
 * Pro+: scale live agents, analytics, API.
 *
 * Fetches the same /api/billing/status payload as useSubscription.
 */

'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import type { PlanTier, SubscriptionStatus } from '@/lib/stripe/types'

/** Tiers that may run/supervise agents and execute orders. */
const AGENT_EXECUTION_TIERS: ReadonlySet<PlanTier | 'none'> = new Set([
  'starter',
  'advanced',
  'professional',
  'enterprise',
])

/** Tiers with multi-exchange grid/DCA libraries + premium data feeds. */
const GRID_DCA_TIERS: ReadonlySet<PlanTier | 'none'> = new Set([
  'advanced',
  'professional',
  'enterprise',
])

/** Tiers with autonomous (unattended) trading agents. */
const AUTONOMOUS_TIERS: ReadonlySet<PlanTier | 'none'> = new Set([
  'advanced',
  'professional',
  'enterprise',
])

export interface PlanEntitlements {
  /** Current plan tier from billing status (`none` when unsubscribed / unknown). */
  tier: PlanTier | 'none'
  /** Operator+: run/supervise live trading agents / full agent chat. */
  canUseAgents: boolean
  /** Operator+: place and execute live orders. */
  canExecuteOrders: boolean
  /** Desk+: multi-exchange grid/DCA strategy libraries. */
  canUseGridDca: boolean
  /** Concurrent live autonomous agents. 0 = none (Free/Operator). */
  maxAutonomousAgents: number
  /** Free+: named paper agents (never live). */
  canUsePaperAgents: boolean
  /** Max paper agent slots. */
  maxPaperAgents: number
  /** Free+: research chat (may burn Desk Credits). */
  canUseResearchChat: boolean
  /** Included Desk Credits / mo. */
  deskCreditsIncluded: number
  /** paper_only | full | none */
  deskCreditsMode: 'paper_only' | 'full' | 'none'
  /** True only for billed Free. Unknown (`none`) is not Free costume. */
  isFree: boolean
  isLoading: boolean
  error: string | null
  refresh: () => Promise<void>
}

export function entitlementsFromTier(tier: PlanTier | 'none'): Pick<
  PlanEntitlements,
  | 'canUseAgents'
  | 'canExecuteOrders'
  | 'canUseGridDca'
  | 'isFree'
  | 'maxAutonomousAgents'
  | 'canUsePaperAgents'
  | 'maxPaperAgents'
  | 'canUseResearchChat'
  | 'deskCreditsIncluded'
  | 'deskCreditsMode'
> {
  const canUseAgents = AGENT_EXECUTION_TIERS.has(tier)
  const isFree = tier === 'free'
  // Paper agents: Free + all paid. none (unsigned) = 0.
  const maxPaperAgents =
    tier === 'free'
      ? 1
      : tier === 'starter'
        ? 1
        : tier === 'advanced'
          ? 3
          : tier === 'professional'
            ? 10
            : tier === 'enterprise'
              ? 50
              : 0
  const deskCreditsIncluded =
    tier === 'free'
      ? 200
      : tier === 'starter'
        ? 1000
        : tier === 'advanced'
          ? 6000
          : tier === 'professional'
            ? 25000
            : tier === 'enterprise'
              ? 40000
              : 0
  const mode: PlanEntitlements['deskCreditsMode'] =
    tier === 'none' ? 'none' : tier === 'free' ? 'paper_only' : deskCreditsIncluded > 0 ? 'full' : 'none'
  return {
    canUseAgents,
    canExecuteOrders: canUseAgents,
    canUseGridDca: GRID_DCA_TIERS.has(tier),
    isFree,
    maxAutonomousAgents: AUTONOMOUS_TIERS.has(tier)
      ? tier === 'advanced'
        ? 1
        : tier === 'professional'
          ? 5
          : tier === 'enterprise'
            ? 20
            : 0
      : 0,
    canUsePaperAgents: maxPaperAgents > 0,
    maxPaperAgents,
    canUseResearchChat: tier === 'free' || canUseAgents,
    deskCreditsIncluded,
    deskCreditsMode: mode,
  }
}

export function usePlanEntitlements(): PlanEntitlements {
  const [tier, setTier] = useState<PlanTier | 'none'>('none')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true)
      const res = await fetch('/api/billing/status', {
        credentials: 'include',
        cache: 'no-store',
      })
      if (!res.ok) throw new Error('Failed to fetch plan status')
      const data = (await res.json()) as {
        subscription?: SubscriptionStatus
        error?: string
      }
      const nextTier: PlanTier | 'none' = data.subscription?.tier ?? 'none'
      setTier(nextTier)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const flags = useMemo(() => entitlementsFromTier(tier), [tier])

  return {
    tier,
    ...flags,
    isLoading,
    error,
    refresh,
  }
}
